import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Notifications inbox surface (T-0109 / E10 → E09-F03 §4).
 *
 * The T-0079 backend exposes two recipient-scoped endpoints — both intentionally
 * flat (not engagement-nested) so the inbox spans every engagement the caller
 * is a recipient on:
 *   - `GET  /api/v1/notifications` — paginated list ordered (unread first,
 *     then created_at DESC) with `unread_count` + opaque `next_cursor`.
 *   - `POST /api/v1/notifications/:id/read` — mark a single delivery read;
 *     naturally idempotent; cross-recipient → 404 (existence-leak guard).
 *
 * "Mark all read" is implemented client-side as a fan-out of POST /read calls
 * over the current page's unread items — the backend does not expose a bulk
 * endpoint today and adding one is out of scope for this UI ticket (flagged
 * as a backend follow-up in the PR body).
 *
 * Severity vocabulary matches `packages/notification-fanout/src/taxonomy.ts →
 * Severity = 'info' | 'warning' | 'high' | 'critical'`. The UI buckets `high`
 * and `critical` together visually per the prototype which only shows three
 * groups (critical / warning / info).
 */

export const NotificationItemSchema = z.object({
  id: z.string().uuid(),
  notification_event_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  title: z.string(),
  summary: z.string(),
  source_link: z.string(),
  channel: z.literal('in_app'),
  event_type: z.string(),
  severity: z.enum(['info', 'warning', 'high', 'critical']),
  is_read: z.boolean(),
  read_at: z.string().nullable(),
  inapp_status: z.string(),
  dispatched_at: z.string().nullable(),
  created_at: z.string(),
});
export type NotificationItem = z.infer<typeof NotificationItemSchema>;

export const NotificationListSchema = z.object({
  items: z.array(NotificationItemSchema),
  unread_count: z.number(),
  next_cursor: z.string().nullable(),
});
export type NotificationList = z.infer<typeof NotificationListSchema>;

export const MarkReadResponseSchema = z.object({
  id: z.string().uuid(),
  is_read: z.boolean(),
  read_at: z.string(),
});
export type MarkReadResponse = z.infer<typeof MarkReadResponseSchema>;

export interface ListInboxOpts {
  unread_only?: boolean;
  cursor?: string;
  limit?: number;
}

export function listInbox(opts: ListInboxOpts = {}): Promise<NotificationList> {
  const query: Record<string, string | number | boolean | undefined> = {
    unread_only: opts.unread_only,
    cursor: opts.cursor,
    limit: opts.limit,
  };
  return apiClient('/api/v1/notifications', NotificationListSchema, { query });
}

export function markRead(notificationId: string): Promise<MarkReadResponse> {
  return apiClient(
    `/api/v1/notifications/${encodeURIComponent(notificationId)}/read`,
    MarkReadResponseSchema,
    { method: 'POST' },
  );
}

export function useNotificationsInboxQuery(opts: ListInboxOpts = {}) {
  return useQuery({
    queryKey: ['notifications', 'inbox', opts] as const,
    queryFn: () => listInbox(opts),
    // The inbox is the primary surface for "what's new"; refresh on
    // focus so a user returning to the tab sees newly-arrived items.
    refetchOnWindowFocus: true,
    // Poll so the bell badge stays fresh without a refocus (Tier 2).
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useMarkReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => markRead(notificationId),
    onSuccess: () => {
      // Any inbox query — including filtered variants — needs to refetch
      // so the unread_count and ordering stay consistent.
      void qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] });
    },
  });
}

const MarkAllReadResponseSchema = z.object({ marked_count: z.number() }).passthrough();

export function useMarkAllReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient('/api/v1/notifications/read-all', MarkAllReadResponseSchema, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] });
    },
  });
}
